import { useCallback, useEffect, useRef, useState } from 'react'
import useCopyToClipboard from 'hooks/useCopyToClipboard'

const DEFAULT_RESET_MS = 1500

/**
 * Clipboard-copy hook with transient "copied" feedback.
 *
 * @param value  Optional text bound at creation. When provided, `handleCopy()`
 *               copies it directly.
 * @param resetMs  Duration (ms) the `isCopied` flag stays true.
 *
 * @returns
 *  - `isCopied`   – true while the feedback timer is active
 *  - `handleCopy` – no-arg handler safe to pass directly to `onClick`
 *                   (uses the bound `value`)
 *  - `copyText`   – accepts text at call-time for dynamic copy targets
 */
function useCopyWithFeedback(value?: string, resetMs = DEFAULT_RESET_MS) {
  const [isCopied, setIsCopied] = useState(false)
  const [, copy] = useCopyToClipboard()
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => { clearTimeout(timerRef.current) }, [])

  const copyText = useCallback(async(text: string) => {
    if (isCopied) return
    const success = await copy(text)
    if (success) {
      setIsCopied(true)
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setIsCopied(false), resetMs)
    }
  }, [copy, isCopied, resetMs])

  const handleCopy = useCallback(async() => {
    if (!value) return
    await copyText(value)
  }, [value, copyText])

  return { isCopied, handleCopy, copyText }
}

export default useCopyWithFeedback
